import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Formik } from 'formik'
import * as Yup from 'yup'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import ConfirmPopup from '../../Popups/ConfirmPopup'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import ToggleField from '../../FormLegacy/FormFieldsV2/ToggleField'
import DateField from '../../FormLegacy/FormFieldsV2/DateField'
import SelectField from '../../FormLegacy/FormFieldsV2/SelectField'
import {
  createPromoCode,
  deletePromoCode,
  formatShopCollectionForSelect,
  selectShopCollectionWithoutGifts,
  selectPromoCodeById,
} from '../../../reducers/Organisation/organisationShopReducer'

const OrganisationPromoCodesModal = () => {
  const { t } = useTranslation(['organisation', 'common'])
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()

  const data = useSelector((state) => selectPromoCodeById(state)(id)) || {}
  const shopProducts = useSelector((state) =>
    formatShopCollectionForSelect(selectShopCollectionWithoutGifts(state)),
  )

  const [formPending, setFormPending] = useState(false)
  const [modalConfirmOpened, setModalConfirmOpened] = useState(false)
  const [formError, setFormError] = useState('')
  const [formSuccess, setFormSuccess] = useState(false)

  const isNew = () => id === 'new'

  const closeModal = () => navigate('/dashboard/organisation/promo-codes')

  const openConfirmModal = (e) => {
    e.preventDefault()
    setModalConfirmOpened(true)
  }

  const closeConfirmModal = () => setModalConfirmOpened(false)

  const cancelPromoCodeChanges = (e) => {
    e.preventDefault()
    closeModal()
  }

  const savePromoCode = (
    { hash, discountAmount, discountType, quantity, expDate, products, all },
    { setFieldError },
  ) => {
    setFormError('')
    setFormPending(true)

    const newPromoCode = {
      hash,
      discountAmount,
      discountType,
      quantity,
      expDate: expDate.valueOf(),
      active: true,
      all,
      products: JSON.stringify([products]),
    }

    if (all) {
      delete newPromoCode['products']
    }

    dispatch(createPromoCode(newPromoCode))
      .then((res) => {
        setFormPending(false)
        setFormSuccess(true)
        navigate(`/dashboard/organisation/promo-codes/${res.id}`)
        setTimeout(() => setFormSuccess(false), 3000)
      })
      .catch((err) => {
        setFormPending(false)
        if (err === 409) {
          setFormError(t('organisation:promoCodes.modal.errors.codeExists'))
          setFieldError('hash', t('organisation:promoCodes.modal.errors.hashExists'))
        } else {
          setFormError(t('organisation:shared.serverError'))
        }
      })
  }

  const handleDeletePromoCode = () => {
    dispatch(deletePromoCode(data.hash))
      .then(() => {
        closeConfirmModal()
        closeModal()
      })
      .catch(() => {
        setFormError(t('organisation:shared.serverError'))
        closeConfirmModal()
      })
  }

  const validationSchema = Yup.object().shape({
    discountAmount: Yup.number().min(1).required(t('common:validation.required')),
    quantity: Yup.number().min(1).required(t('common:validation.required')),
  })

  return (
    <div className={`modal modal--organisation modal--promo-code ${id ? 'is-active' : ''}`}>
      <div className="modal-background" onClick={cancelPromoCodeChanges} />
      <div className="modal-content">
        <div className="box">
          <header className="modal__header">
            <span>
              {isNew()
                ? t('organisation:promoCodes.modal.titleNew')
                : t('organisation:promoCodes.modal.titleEdit')}
            </span>
            <button
              className="modal-close is-large"
              aria-label={t('organisation:shared.closeAriaLabel')}
              onClick={cancelPromoCodeChanges}
            />
          </header>
          <Formik
            enableReinitialize
            initialValues={{
              hash: data.hash || '',
              discountAmount: data.discountAmount || '',
              discountType: data.discountType || 'value',
              quantity: data.quantity || '',
              expDate: (data.expDate && moment(data.expDate)) || moment().add(30, 'days'),
              products: (data.products && data.products.length > 0 && data.products[0].id) || '',
              all: data.all ?? true,
            }}
            onSubmit={savePromoCode}
            validationSchema={validationSchema}
          >
            {({
              handleSubmit,
              handleChange,
              values,
              errors,
              touched,
              setFieldTouched,
              setFieldValue,
              dirty,
            }) => (
              <form onSubmit={handleSubmit}>
                <div className="modal__inner form">
                  <div className="modal__section">
                    <TextField
                      name="hash"
                      placeholder={t('organisation:promoCodes.modal.hash.placeholder')}
                      label={t('organisation:promoCodes.modal.hash.label')}
                      value={values.hash}
                      onChange={handleChange}
                      isError={touched.hash && !!errors.hash}
                      disabled={!isNew()}
                    />
                  </div>
                  <div className="modal__section">
                    <div className="columns">
                      <div className="column">
                        <TextField
                          type="number"
                          name="discountAmount"
                          placeholder={t('organisation:promoCodes.modal.discount.placeholder')}
                          label={t('organisation:promoCodes.modal.discount.label')}
                          onChange={handleChange}
                          value={values.discountAmount}
                          isError={touched.discountAmount && !!errors.discountAmount}
                          onBlur={() => setFieldTouched('discountAmount')}
                          disabled={!isNew()}
                        />
                      </div>
                      <div className="column modal__percentage">
                        <ToggleField
                          name="discountType"
                          label={t('organisation:promoCodes.modal.percentage')}
                          value={values.discountType === 'percentage'}
                          onChange={(e) =>
                            setFieldValue('discountType', e.target.checked ? 'percentage' : 'value')
                          }
                          disabled={!isNew()}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal__section">
                    <div className="columns">
                      <div className="column">
                        <TextField
                          type={'number'}
                          name={'quantity'}
                          placeholder={t('organisation:promoCodes.modal.quantity.placeholder')}
                          label={t('organisation:promoCodes.modal.quantity.label')}
                          onChange={handleChange}
                          value={values.quantity}
                          isError={touched.quantity && !!errors.quantity}
                          onBlur={() => setFieldTouched('quantity')}
                          disabled={!isNew()}
                        />
                      </div>
                      <div className="column">
                        <DateField
                          name={'expDate'}
                          value={values.expDate}
                          label={t('organisation:promoCodes.modal.expDate.label')}
                          onChange={handleChange}
                          dateFormat="MM/dd/yyyy"
                          popperPlacement="top-start"
                          disabled={!isNew()}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal__section">
                    <div className="columns">
                      <div className="column is-6">
                        <SelectField
                          name={'products'}
                          label={t('organisation:promoCodes.modal.products.label')}
                          options={[
                            {
                              value: 'all',
                              label: t('organisation:promoCodes.modal.products.allOption'),
                            },
                            ...shopProducts,
                          ]}
                          onChange={(e) => {
                            const val = e.target.value
                            if (val === 'all') {
                              setFieldValue('all', true)
                              setFieldValue('products', 'all')
                            } else {
                              setFieldValue('all', false)
                              setFieldValue('products', val)
                            }
                          }}
                          value={values.all ? 'all' : values.products}
                          disabled={!isNew()}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal__section modal__actions">
                    {!isNew() && (
                      <span className="link" onClick={openConfirmModal}>
                        {t('organisation:promoCodes.modal.delete')}
                      </span>
                    )}
                    <button
                      type="button"
                      className="button button-gray is-block"
                      onClick={cancelPromoCodeChanges}
                    >
                      {t('common:actions.cancel')}
                    </button>
                    {isNew() && (
                      <button
                        type="submit"
                        className={`button button-primary is-block ${
                          formPending ? 'is-loading' : ''
                        }`}
                        disabled={!dirty || formPending}
                      >
                        {t('common:actions.save')}
                      </button>
                    )}
                    <p className="help is-danger">{formError}</p>
                    {formSuccess && (
                      <p className="help is-success">
                        {t('organisation:promoCodes.modal.successMessage')}
                      </p>
                    )}
                  </div>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
      <ConfirmPopup
        title={t('organisation:promoCodes.modal.deleteConfirm.title')}
        cancelText={t('common:actions.cancel')}
        confirmText={t('common:actions.confirm')}
        isOpen={modalConfirmOpened}
        cancelCallback={closeConfirmModal}
        confirmCallback={handleDeletePromoCode}
      />
    </div>
  )
}

export default OrganisationPromoCodesModal
